'use client'

import { ComponentProps, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import {
  FigsLoader,
  HeartButton,
  HeartButtonContent,
  ScreenReaderOnly,
  zIndexForeground,
} from '@syconium/little-miss-figgy'

import { trackEvent } from '../../../lib/analytics'
import { useFavorites } from '../../_providers/FavoritesProvider.client'
import { useSession } from '../../_providers/SessionProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'

type FavoritesButtonProps = {
  /** The handle of the target product that will be added/removed from the current user's favorites. */
  productGroupHandle: string
  /** The color variant of the target product that will be added/removed from the current user's favorites. */
  colorKey: string
  /** The fit variant of the target product that will be added/removed from the current user's favorites. */
  fitKey: string
  /** The size variant of the target product that will be added/removed from the current user's favorites. */
  sizeKey: string | null | undefined
  /** The image to be displayed in the toast notification. */
  productGroupImageSrc: string | null | undefined
  /** How large the heart icon should be. */
  iconWidth?: string
} & ComponentProps<typeof HeartButton>

/** Button component that allows users to add/remove products from their favorites list. */
export function FavoritesButton({
  productGroupHandle,
  colorKey,
  fitKey,
  sizeKey,
  productGroupImageSrc,
  iconWidth = '24px',
  ...rest
}: FavoritesButtonProps) {
  const { favorites, status: favoritesContextStatus, removeFavorite, addFavorite } = useFavorites()
  const favoritesContextPending =
    favoritesContextStatus === 'pending' || favoritesContextStatus === 'idle'

  const [updatePending, setUpdatePending] = useState(false)
  const [allowLoadingVisual, setAllowLoadingVisual] = useState(false)
  const anythingPending = updatePending || favoritesContextPending

  const {
    attributes: { visitorId },
  } = useSession()
  const translations = useTranslation()

  const matchingFavorite = useMemo(
    () =>
      favorites.find(favorite => {
        return favorite?.productGroupHandle === productGroupHandle && favorite?.color === colorKey
      }),
    [favorites, productGroupHandle, colorKey]
  )

  const id = matchingFavorite?.id
  const filled = !!id

  const handleClick = async () => {
    try {
      setUpdatePending(true)
      if (filled) {
        if (id) {
          await removeFavorite({ id, visitorId })
        }
      } else {
        const { errors, data } = await addFavorite({
          input: {
            productGroupHandle: productGroupHandle,
            color: colorKey,
            fit: fitKey,
            size: sizeKey,
          },
          visitorId,
        })
        if ((errors && errors.length > 0) || !data) {
          window.dispatchEvent(
            new CustomEvent('open-toast-notification', {
              detail: {
                type: 'favorites-error-toast-notification',
                payload: { productHandle: productGroupHandle, toastImage: productGroupImageSrc },
              },
            })
          )
        } else {
          window.dispatchEvent(
            new CustomEvent('open-toast-notification', {
              detail: {
                type: 'favorites-add-toast-notification',
                payload: { productHandle: productGroupHandle, toastImage: productGroupImageSrc },
              },
            })
          )
        }
      }
    } finally {
      setUpdatePending(false)
    }
  }

  useEffect(() => {
    if (updatePending !== allowLoadingVisual) {
      if (updatePending === true) {
        const delayedLoadingIndicator = setTimeout(() => {
          setAllowLoadingVisual(true)
        }, 750)

        return () => {
          window.clearTimeout(delayedLoadingIndicator)
        }
      } else {
        setAllowLoadingVisual(false)
      }
    }
    return () => null
  }, [allowLoadingVisual, updatePending])

  if (favoritesContextStatus === 'rejected') return null

  return (
    <HeartButton
      type='button'
      onClick={handleClick}
      filled={filled}
      disabled={anythingPending}
      aria-pressed={`${filled}`}
      {...trackEvent({
        action: 'click',
        category: 'Favorites',
        label: filled ? 'Remove Favorite' : 'Add Favorite',
        value: productGroupHandle,
      })}
      {...rest}
    >
      {anythingPending && allowLoadingVisual ? (
        <FigsLoader size={iconWidth} title={translations.magnolia.general.loading} />
      ) : (
        <>
          {anythingPending ? (
            <ScreenReaderOnly>{translations.magnolia.general.loading}</ScreenReaderOnly>
          ) : (
            <ScreenReaderOnly>{translations.favorites.favoriteLabel}</ScreenReaderOnly>
          )}
          <HeartButtonContent aria-hidden='true' filled={filled} width={iconWidth} />
        </>
      )}
    </HeartButton>
  )
}

/** Variant of `FavoritesButton` that is absolutely positioned to an enclosing relative wrapper. */
export const FloatingFavoritesButton = styled(FavoritesButton)`
  && {
    position: absolute;
    top: ${o => o.theme.spacing(2)};
    right: ${o => o.theme.spacing(2)};
    z-index: ${zIndexForeground};
    max-width: 32px;
  }
`
